import { Alert, Box, Button, Grid, Skeleton, Stack, Typography } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Link as RouterLink } from "react-router";
import dayjs from "dayjs";
import { useRigOfWeekPast } from "../hooks/useApiQueries.js";
import { SEO } from "../components/SEO.js";
import { NeonEyebrow, NeonDivider, NEON_GLOW_SOFT } from "../components/Neon.js";
import { proxied, isLikelyImageUrl } from "../lib/imageProxy.js";
import { displaySource } from "../lib/displayHelpers.js";

export function RigOfWeekArchive() {
  const { data, isLoading } = useRigOfWeekPast(40);

  if (isLoading) {
    return (
      <Grid container spacing={2} sx={{ mt: 1 }}>
        {Array.from({ length: 8 }).map((_, i) => (
          <Grid key={i} size={{ xs: 12, sm: 6, md: 3 }}>
            <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 1 }} />
          </Grid>
        ))}
      </Grid>
    );
  }

  const items = data?.items ?? [];

  return (
    <>
      <SEO
        title="Rig of the Week — Archive"
        description="Every Rig of the Week the Corian Park team has picked since launch."
        canonical="https://corianpark.au/rig-of-week/archive"
      />

      <Stack direction="row" spacing={2} sx={{ alignItems: "center", mb: 1 }}>
        <Button
          component={RouterLink}
          to="/rig-of-the-week"
          size="small"
          sx={{ color: "text.secondary", fontSize: 13 }}
        >
          ← Current rig
        </Button>
      </Stack>

      <NeonEyebrow glow>RIG OF THE WEEK</NeonEyebrow>
      <Typography variant="h2" sx={{ fontSize: { xs: 32, md: 40 }, fontWeight: 900, mt: 1, mb: 1 }}>
        Archive
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Every machine we've featured — latest first.
      </Typography>

      {items.length === 0 && (
        <Alert severity="info">No past rigs to show yet.</Alert>
      )}

      <Grid container spacing={2}>
        {items.map((p) => {
          const equipLabel =
            (p.equipment?.make && p.equipment?.model
              ? `${p.equipment.make} ${p.equipment.model}`
              : p.equipment?.make ?? p.equipment?.model) ??
            p.operatorName ??
            "Past rig";
          const subtitle = displaySource(p.operatorName) ?? "";
          const img = p.imageUrl;
          return (
            <Grid key={p.id} size={{ xs: 12, sm: 6, md: 3 }}>
              <Box
                sx={{
                  borderRadius: 1,
                  overflow: "hidden",
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition:
                    "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: NEON_GLOW_SOFT,
                    borderColor: "primary.main",
                  },
                }}
              >
                {isLikelyImageUrl(img) ? (
                  <Box sx={{ position: "relative", aspectRatio: "16 / 10" }}>
                    <Box
                      component="img"
                      src={proxied(img!)}
                      alt={equipLabel}
                      loading="lazy"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                  </Box>
                ) : (
                  <Box
                    sx={{
                      aspectRatio: "16 / 10",
                      bgcolor: "action.hover",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      No image
                    </Typography>
                  </Box>
                )}
                <Box sx={{ p: 2, flex: 1 }}>
                  <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", mb: 0.5 }}>
                    <CalendarMonthIcon sx={{ fontSize: 13, color: "text.secondary" }} />
                    <Typography variant="caption" color="text.secondary">
                      {dayjs(p.weekStarting).format("D MMM YYYY")}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 700, lineHeight: 1.2 }}
                  >
                    {equipLabel}
                  </Typography>
                  {subtitle && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mt: 0.5 }}
                    >
                      {subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Grid>
          );
        })}
      </Grid>

      {items.length >= 40 && (
        <Box sx={{ mt: 4, textAlign: "center" }}>
          <NeonDivider sx={{ mb: 3 }} />
          <Typography variant="body2" color="text.secondary">
            Showing the 40 most recent rigs.
          </Typography>
        </Box>
      )}
    </>
  );
}
